import { useParams } from 'react-router-dom';
import { useState, useEffect, useCallback } from 'react';
import { Text, Card, Button } from '@hawkins/components';
import NetflixGradientBackground from 'src/components/elements/NetflixGradientBackground';
import LoadingText from 'src/components/elements/LoadingText';

// Sub-components
const TokenDisplay = ({ token }: { token?: string }) => (
  <Card style={{
    backgroundColor: '#333',
    border: '1px solid #737373',
    borderRadius: '4px',
    padding: '20px',
    marginBottom: '20px'
  }}>
    <Text style={{ color: '#999', fontSize: '14px', marginBottom: '8px' }}>
      Token:
    </Text>
    <Text style={{ 
      color: 'white', 
      fontFamily: 'monospace',
      wordBreak: 'break-all'
    }}>
      {token || 'No token provided'}
    </Text>
  </Card>
);

const LoadingState = () => (
  <LoadingText>
    Verifying token...
  </LoadingText>
);

const ErrorDisplay = ({ error, errorDetails }: { error?: string; errorDetails?: unknown }) => (
  <Card style={{
    backgroundColor: '#660000',
    border: '1px solid #cc0000',
    borderRadius: '4px',
    padding: '20px',
    marginBottom: '20px'
  }}>
    <Text style={{ color: '#ffcccc', fontSize: '14px', marginBottom: '8px' }}>
      Error:
    </Text>
    <Text style={{ color: '#ffcccc', marginBottom: '12px' }}>
      {error}
    </Text>
    {errorDetails != null && (
      <>
        <Text style={{ color: '#ffcccc', fontSize: '14px', marginBottom: '8px' }}>
          Details:
        </Text>
        <Text style={{ 
          color: '#ffcccc', 
          fontFamily: 'monospace',
          fontSize: '12px',
          whiteSpace: 'pre-wrap'
        }}>
          {String(typeof errorDetails === 'string' ? errorDetails : JSON.stringify(errorDetails, null, 2))}
        </Text>
      </>
    )}
  </Card>
);

const UserInfoDisplay = ({ userInfo, username, domain, userId }: {
  userInfo?: { fullName: string };
  username?: string;
  domain?: string;
  userId?: string;
}) => (
  <Card style={{
    backgroundColor: '#006600',
    border: '1px solid #00cc00',
    borderRadius: '4px',
    padding: '20px',
    marginBottom: '20px'
  }}>
    <Text style={{ color: '#ccffcc', fontSize: '14px', marginBottom: '12px' }}>
      ✓ Token Valid - User Information:
    </Text>
    <div style={{ marginBottom: '8px' }}>
      <Text style={{ color: '#ccffcc', fontSize: '14px' }}>
        <strong>Name:</strong> {userInfo?.fullName}
      </Text>
    </div>
    <div style={{ marginBottom: '8px' }}>
      <Text style={{ color: '#ccffcc', fontSize: '14px' }}>
        <strong>Email:</strong> {username}
      </Text>
    </div>
    <div style={{ marginBottom: '8px' }}>
      <Text style={{ color: '#ccffcc', fontSize: '14px' }}>
        <strong>Domain:</strong> {domain}
      </Text>
    </div>
    <div>
      <Text style={{ color: '#ccffcc', fontSize: '14px' }}>
        <strong>User ID:</strong> {userId}
      </Text>
    </div>
  </Card>
);

const PasswordPolicyDisplay = ({ passwordPolicy }: {
  passwordPolicy?: {
    type: string;
    minLength: number;
    requiresDigits: boolean;
    requiresUppercase: boolean;
    requiresLowercase: boolean;
    requiresSpecialChars: boolean;
    description: string;
  };
}) => (
  <Card style={{
    backgroundColor: '#333',
    border: '1px solid #737373',
    borderRadius: '4px',
    padding: '20px',
    marginBottom: '20px'
  }}>
    <Text style={{ color: '#ccc', fontSize: '14px', marginBottom: '12px' }}>
      Password Policy ({passwordPolicy?.type}):
    </Text>
    <div style={{ marginBottom: '8px' }}>
      <Text style={{ color: 'white', fontSize: '14px' }}>
        {passwordPolicy?.description}
      </Text>
    </div>
    <div style={{ marginTop: '12px' }}>
      <Text style={{ color: '#ccc', fontSize: '14px', marginBottom: '8px' }}>
        Requirements:
      </Text>
      <div style={{ paddingLeft: '16px' }}>
        <div>
          <Text style={{ color: 'white', fontSize: '12px' }}>
            • Minimum {passwordPolicy?.minLength} characters
          </Text>
        </div>
        <div>
          <Text style={{ color: 'white', fontSize: '12px' }}>
            • Digits required: {passwordPolicy?.requiresDigits ? 'Yes' : 'No'}
          </Text>
        </div>
        <div>
          <Text style={{ color: 'white', fontSize: '12px' }}>
            • Uppercase required: {passwordPolicy?.requiresUppercase ? 'Yes' : 'No'}
          </Text>
        </div>
        <div>
          <Text style={{ color: 'white', fontSize: '12px' }}>
            • Special characters required: {passwordPolicy?.requiresSpecialChars ? 'Yes' : 'No'}
          </Text>
        </div>
      </div>
    </div>
  </Card>
);

const MFAStatusDisplay = ({ requiresMFA }: { requiresMFA?: boolean }) => (
  <Card style={{
    backgroundColor: requiresMFA ? '#664400' : '#004466',
    border: requiresMFA ? '1px solid #cc8800' : '1px solid #0088cc',
    borderRadius: '4px',
    padding: '20px',
    marginBottom: '20px'
  }}>
    <Text style={{ 
      color: requiresMFA ? '#ffcc99' : '#99ccff', 
      fontSize: '14px',
      marginBottom: '8px' 
    }}>
      Multi-Factor Authentication:
    </Text>
    <Text style={{ 
      color: requiresMFA ? '#ffcc99' : '#99ccff', 
      fontSize: '14px' 
    }}>
      {requiresMFA ? 
        '🔒 MFA Required - Additional authentication will be needed' : 
        '✓ MFA Not Required - Password reset can proceed directly'
      }
    </Text>
  </Card>
);

const UserStatusDisplay = ({ userStatus }: { 
  userStatus?: {
    isNewHire: boolean;
    isIdpMigrated: boolean;
    loginAttempts: unknown;
  } 
}) => (
  <Card style={{
    backgroundColor: '#444',
    border: '1px solid #666',
    borderRadius: '4px',
    padding: '20px',
    marginBottom: '20px'
  }}>
    <Text style={{ color: '#fff', fontSize: '14px', marginBottom: '12px' }}>
      User Status Details:
    </Text>
    <div style={{ marginBottom: '8px' }}>
      <Text style={{ color: '#ccc', fontSize: '14px' }}>
        <strong>New Hire:</strong> {userStatus?.isNewHire ? 
          '✅ Yes (never logged in)' : 
          '❌ No (has login history)'
        }
      </Text>
    </div>
    <div style={{ marginBottom: '8px' }}>
      <Text style={{ color: '#ccc', fontSize: '14px' }}>
        <strong>IDP Migrated:</strong> {userStatus?.isIdpMigrated ? 
          '✅ Yes (eligible for MFA)' : 
          '❌ No (not migrated to IDP)'
        }
      </Text>
    </div>
    <div>
      <Text style={{ color: '#ccc', fontSize: '14px' }}>
        <strong>Login History:</strong> 
      </Text>
      <div style={{ marginTop: '4px', paddingLeft: '16px' }}>
        <Text style={{ 
          color: '#aaa', 
          fontSize: '12px', 
          fontFamily: 'monospace',
          wordBreak: 'break-all'
        }}>
          {userStatus?.loginAttempts ? JSON.stringify(userStatus.loginAttempts) : 'No login attempts data'}
        </Text>
      </div>
    </div>
  </Card>
);

const DebugResponseDisplay = ({ data }: { data: unknown }) => (
  <Card style={{
    backgroundColor: '#222',
    border: '1px solid #555',
    borderRadius: '4px',
    padding: '20px',
    marginBottom: '20px'
  }}>
    <Text style={{ color: '#ccc', fontSize: '14px', marginBottom: '8px' }}>
      Password Reset App Backend Response (Debug):
    </Text>
    <Text style={{ 
      color: '#ccc', 
      fontFamily: 'monospace',
      fontSize: '11px',
      whiteSpace: 'pre-wrap'
    }}>
      {String(typeof data === 'string' ? data : JSON.stringify(data, null, 2))}
    </Text>
  </Card>
);


const NetflixLogo = () => (
  <div style={{ marginBottom: '40px' }}>
    <img 
      src="/nflx-logo-classic@150w.png" 
      alt="Netflix" 
      style={{ height: '80px' }}
    />
  </div>
);

interface VerificationResult {
  success: boolean;
  tokenValid: boolean;
  passwordPolicy?: {
    type: string;
    minLength: number;
    requiresDigits: boolean;
    requiresUppercase: boolean;
    requiresLowercase: boolean;
    requiresSpecialChars: boolean;
    description: string;
  };
  requiresMFA?: boolean;
  testEnvOnly?: {
    userId: string;
    username: string;
    domain: string;
    userStatus: {
      isNewHire: boolean;
      isIdpMigrated: boolean;
      loginAttempts: unknown;
    };
    userInfo: {
      fullName: string;
      primaryEmail: string;
      givenName: string;
      familyName: string;
    };
    adEligibility: unknown;
  };
  error?: string;
  errorDetails?: unknown;
}

function ResetPageDebug() {
  const { resetToken } = useParams<{ resetToken: string }>();
  const [verificationResult, setVerificationResult] = useState<VerificationResult | null>(null);
  const [loading, setLoading] = useState(false);

  const verifyToken = useCallback(async () => {
    if (!resetToken) {
      setVerificationResult({
        success: false,
        tokenValid: false,
        error: 'No token provided'
      });
      return;
    }

    setLoading(true);
    
    try {
      const response = await fetch('/api/verify-reset-token', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({
          token: resetToken
        })
      });
      
      const data = await response.json();
      setVerificationResult(data);
      
      if (data.success) {
        console.log('Token verification successful:', data);
      } else {
        console.error('Token verification failed:', data.error);
      }
    } catch (error) {
      console.error('Error verifying token:', error);
      setVerificationResult({
        success: false,
        tokenValid: false,
        error: 'An error occurred while verifying the token'
      });
    } finally {
      setLoading(false);
    }
  }, [resetToken]);

  useEffect(() => {
    if (resetToken) {
      verifyToken();
    }
  }, [resetToken, verifyToken]);

  return (
    <NetflixGradientBackground>
      <div style={{
        display: 'flex',
        alignItems: 'flex-start',
        justifyContent: 'center',
        minHeight: '100vh',
        padding: '20px',
        paddingTop: '40px'
      }}>
        <div style={{
          display: 'flex',
          flexDirection: 'column',
          alignItems: 'center',
          width: '100%',
          maxWidth: '450px'
        }}>
          <NetflixLogo />

          <Card style={{
            backgroundColor: 'rgba(0, 0, 0, 0.75)',
            padding: '48px 68px 36px',
            borderRadius: '4px',
            width: '100%',
            border: 'none'
          }}>
            <Text 
              style={{ 
                color: 'white', 
                fontSize: '32px',
                fontWeight: '700',
                marginBottom: '24px',
                textAlign: 'center'
              }}
            >
              Reset Token Verification
            </Text>

            <TokenDisplay token={resetToken} />

            {loading && <LoadingState />}

            {verificationResult && !verificationResult.success && (
              <ErrorDisplay 
                error={verificationResult.error}
                errorDetails={verificationResult.errorDetails}
              />
            )}

            {verificationResult && verificationResult.success && (
              <>
                <UserInfoDisplay 
                  userInfo={verificationResult.testEnvOnly?.userInfo}
                  username={verificationResult.testEnvOnly?.username}
                  domain={verificationResult.testEnvOnly?.domain}
                  userId={verificationResult.testEnvOnly?.userId}
                />
                <PasswordPolicyDisplay passwordPolicy={verificationResult.passwordPolicy} />
                <MFAStatusDisplay requiresMFA={verificationResult.requiresMFA} />
                <UserStatusDisplay userStatus={verificationResult.testEnvOnly?.userStatus} />
                <DebugResponseDisplay data={verificationResult} />
              </>
            )}

            <Button
              onClick={verifyToken}
              disabled={loading || !resetToken}
              style={{
                width: '100%',
                backgroundColor: '#e50914',
                border: 'none',
                borderRadius: '4px',
                color: 'white',
                fontSize: '16px',
                fontWeight: '700',
                padding: '12px 16px',
                cursor: 'pointer',
                height: '48px',
                minHeight: '48px',
                boxSizing: 'border-box'
              }}
            >
              {loading ? 'Verifying...' : 'Verify Token Again'}
            </Button>
          </Card>
        </div>
      </div>
    </NetflixGradientBackground>
  );
}

export default ResetPageDebug;